import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { getConfig } from '~/utils/config'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const config = getConfig()

  const user = ref<any>(null)
  const isAuthenticated = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Helper para manejar respuestas
  const handleResponse = async (res: Response) => {
    const data = await res.json().catch(() => null)

    if (!res.ok) {
      throw new Error(data?.message || 'Error en la petición')
    }

    return data
  }

  // -----------------------------
  // Login con credenciales
  // -----------------------------
  async function loginWithCredentials({ email, password }: { email: string; password: string }) {
    loading.value = true
    error.value = null

    try {
      const res = await fetch(`${config.apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      })

      await handleResponse(res)

      await fetchUser()

      await router.push('/')
      return { success: true }
    } catch (err: any) {
      error.value = err?.message || 'Error al iniciar sesión'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // -----------------------------
  // Logout
  // -----------------------------
  async function logout() {
    localStorage.removeItem('authUser')
    localStorage.removeItem('isAuthenticated')

    try {
      const res = await fetch(`${config.apiUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })

      await handleResponse(res)
    } catch (err) {
      console.error('Error en logout', err)
    }

    user.value = null
    isAuthenticated.value = false
  }

  // -----------------------------
  // Obtener usuario actual
  // -----------------------------
  async function fetchUser() {
    loading.value = true

    try {
      const res = await fetch(`${config.apiUrl}/user/me`, {
        method: 'GET',
        credentials: 'include',
      })

      const data = await handleResponse(res)

      user.value = data
      isAuthenticated.value = true
    } catch (err) {
      user.value = null
      isAuthenticated.value = false
    } finally {
      loading.value = false
    }
  }

  // -----------------------------
  // Sign Up
  // -----------------------------
  async function signUp({ email, password }: { email: string; password: string }) {
    try {
      const res = await fetch(`${config.apiUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      })

      await handleResponse(res)

      return { success: true }
    } catch (err: any) {
      error.value = err?.message || 'Error al registrar usuario'
      return { success: false, error: error.value }
    }
  }

  return {
    user,
    isAuthenticated,
    loading,
    error,
    loginWithCredentials,
    logout,
    fetchUser,
    signUp,
  }
})
